import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";
import "./styles/LeagueCreatePage.css";

const K_BLUE = "#0D47A1";
const K_YELLOW = "#FFF70D";

const LeagueCreatePage = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);

  React.useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const validationError = useMemo(() => {
    const value = name.trim();
    if (!value) return t("leagueCreate.validation.required");
    if (value.length < 3) return t("leagueCreate.validation.min");
    if (value.length > 40) return t("leagueCreate.validation.max");
    return null;
  }, [name, t]);

  const parseJsonSafe = async (res) => {
    try {
      const text = await res.text();
      if (!text || text.trimLeft().startsWith("<")) return {};
      return JSON.parse(text);
    } catch {
      return {};
    }
  };

  const submit = async () => {
    if (submitting) return;
    setError(null);

    const vErr = validationError;
    if (vErr) {
      setError(vErr);
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${apiUrl}/createLeague`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name.trim() }),
      });

      const body = await parseJsonSafe(res);

      const msg = (body.message ?? "").toString().toLowerCase();
      const code = (body.code ?? "").toString();

      const nameTaken =
        res.status === 409 ||
        code === "NAME_TAKEN" ||
        msg.includes("pris") ||
        msg.includes("taken") ||
        msg.includes("already");

      if (nameTaken) {
        setSubmitting(false);
        setError(t("leagueCreate.errors.nameTaken"));
        return;
      }

      if (!res.ok) {
        throw new Error(body.message ?? `HTTP ${res.status}`);
      }

      if (!body.leagueId) {
        throw new Error(t("leagueCreate.errors.missingLeagueId"));
      }

      setSubmitting(false);
      setSuccessOpen(true);
    } catch (e) {
      setSubmitting(false);
      setError(t("leagueCreate.errors.generic", { message: e?.message ?? String(e) }));
    }
  };

  const onSubmitForm = (e) => {
    e.preventDefault();
    submit();
  };

  const closeSuccess = () => {
    setSuccessOpen(false);
    navigate("/profil");
  };

  return (
    <div className="lc-page">
      <div className="lc-topbar">
        <button
          className="lc-back"
          onClick={() => navigate(-1)}
          aria-label={t("leagueCreate.back")}
        >
          ←
        </button>
        <div className="lc-title">{t("leagueCreate.title")}</div>
        <div style={{ width: 34 }} />
      </div>

      <div className="lc-content">
        <form className="lc-form" onSubmit={onSubmitForm}>
          <div className="lc-section-title">
            <div className="lc-yellow-bar" />
            <span>{t("leagueCreate.sectionTitle")}</span>
          </div>

          <label className="lc-label" htmlFor="leagueName">
            {t("leagueCreate.form.leagueNameLabel")}
          </label>

          <input
            id="leagueName"
            className="lc-input"
            placeholder={t("leagueCreate.form.placeholder")}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            maxLength={40}
            autoComplete="off"
          />

          {error ? <div className="lc-error">{error}</div> : null}

          <button className="lc-submit" type="submit" disabled={submitting}>
            {submitting ? t("leagueCreate.actions.submitting") : t("leagueCreate.actions.submit")}
          </button>
        </form>
      </div>

      {successOpen ? (
        <div className="lc-modal-overlay" role="dialog" aria-modal="true">
          <div className="lc-modal">
            <div className="lc-modal-header">
              <div className="lc-modal-header-left">
                <span className="lc-trophy" aria-hidden="true">
                  🏆
                </span>
                <span>{t("leagueCreate.success.title")}</span>
              </div>
            </div>

            <div className="lc-modal-body">
              {t("leagueCreate.success.messageStart")} <b>{name.trim()}</b>
              {t("leagueCreate.success.messageEnd")}
            </div>

            <div className="lc-modal-actions">
              <button className="lc-modal-ok" onClick={closeSuccess}>
                {t("leagueCreate.success.ok")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <style>{`
        :root{
          --lc-blue:${K_BLUE};
          --lc-yellow:${K_YELLOW};
        }
      `}</style>
    </div>
  );
};

export default LeagueCreatePage;
